import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  styled,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton
} from '@mui/material';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import useCompanySettings from '../hooks/useCompanySettings'; // Merkezi şirket ayarlarını ekledik
import { pricingTable } from './Payment'; // Paket limiti kontrolü için pricingTable'ı import et
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
}));

function RegisterPage() {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'user' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingActionData, setPendingActionData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const apiBaseUrl = 'https://pdks.bytebridge.com.tr/api';

  // Merkezi şirket ayarlarını ekledik
  const settings = useCompanySettings();

  // Kullanıcı limiti kontrol fonksiyonu
  const isUserLimitExceeded = () => {
    if (!settings || !settings.package || !users) return false;
    // Aktif paketi bul
    const activePackage = pricingTable.find(p => p.paket === settings.package || p.type === settings.package);
    if (!activePackage) return false;
    return users.length >= (activePackage.employeeCount || 0);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = () => {
    axios.get(`${apiBaseUrl}/register`)
      .then(response => setUsers(response.data))
      .catch(err => {
        setError('Kullanıcılar alınırken hata oluştu.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const showSnackbar = (severity, msg) => {
    setSnackbarSeverity(severity);
    if (severity === 'success') {
      setSuccess(msg);
      setError('');
    } else {
      setError(msg);
      setSuccess('');
    }
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleOpenConfirmDialog = (action, data = null) => {
    setPendingAction(action);
    setPendingActionData(data);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setPendingAction(null);
    setPendingActionData(null);
  };

  const handleConfirmAction = () => {
    if (pendingAction === 'add') {
      handleAddUserConfirmed();
    } else if (pendingAction === 'delete') {
      handleDeleteUserConfirmed();
    } else if (pendingAction === 'update') {
      handleUpdateUserConfirmed();
    }
    handleCloseConfirmDialog();
  };

  const handleAddUser = () => {
    if (!newUser.email || !newUser.password) {
      showSnackbar('warning', 'Lütfen e-posta ve şifre girin.');
      return;
    }
    if (isUserLimitExceeded()) {
      showSnackbar('error', 'Kullanıcı limiti aşıldı. Lütfen paketinizi yükseltin.');
      return;
    }
    handleOpenConfirmDialog('add', { ...newUser });
  };

  const handleAddUserConfirmed = () => {
    axios.post(`${apiBaseUrl}/register`, newUser)
      .then(response => {
        showSnackbar('success', response.data.message);
        setNewUser({ email: '', password: '', role: 'user' });
        fetchUsers();
      })
      .catch(err => {
        showSnackbar('error', err.response?.data?.message || 'Kullanıcı eklenirken hata oluştu.');
      });
  };

  const handleEditUser = (user) => {
    setNewUser({ email: user.email, password: '', role: user.role });
    setEditUserId(user.id);
    setEditMode(true);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditMode(false);
    setEditUserId(null);
    setNewUser({ email: '', password: '', role: 'user' });
  };

  const handleUpdateUser = () => {
    if (!newUser.email) {
      showSnackbar('warning', 'Lütfen e-posta girin.');
      return;
    }
    handleOpenConfirmDialog('update', { ...newUser, id: editUserId });
    setEditDialogOpen(false);
  };

  const handleUpdateUserConfirmed = () => {
    axios.put(`${apiBaseUrl}/register/${pendingActionData.id}`, pendingActionData)
      .then(response => {
        showSnackbar('success', response.data.message);
        setNewUser({ email: '', password: '', role: 'user' });
        setEditMode(false);
        setEditUserId(null);
        fetchUsers();
      })
      .catch(err => {
        showSnackbar('error', err.response?.data?.message || 'Kullanıcı güncellenirken hata oluştu.');
      });
  };

  const handleDeleteUser = (userId) => {
    handleOpenConfirmDialog('delete', { userId });
  };

  const handleDeleteUserConfirmed = () => {
    axios.delete(`${apiBaseUrl}/register/${pendingActionData.userId}`)
      .then(response => {
        showSnackbar('success', response.data.message);
        fetchUsers();
      })
      .catch(err => {
        showSnackbar('error', err.response?.data?.message || 'Kullanıcı silinirken hata oluştu.');
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Tooltip title="Kayıt olmak için bilgilerinizi giriniz.">
          <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
        </Tooltip>
        <Typography variant="h4" gutterBottom>
          {/* Örnek: Şirket adı üst bilgi veya form başlığı olarak gösterilebilir */}
          {settings?.companyName || 'Kullanıcı Yönetimi'}
        </Typography>
      </div>

      <StyledPaper elevation={3}>
        <Typography variant="h6" gutterBottom>
          Kullanıcı Listesi
        </Typography>
        <TableContainer>
          <Table aria-label="users table">
            <TableHead>
              <TableRow>
                <StyledTableCell>E-posta</StyledTableCell>
                <StyledTableCell>Rol</StyledTableCell>
                <StyledTableCell>İşlemler</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <IconButton color="error" onClick={() => handleDeleteUser(user.id)}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton color="warning" onClick={() => handleEditUser(user)} sx={{ ml: 1 }}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={users.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </StyledPaper>

      <StyledPaper elevation={3}>
        <Typography variant="h6" gutterBottom>
          {/* Örnek: Şirket adı form başlığı olarak gösterilebilir */}
          {settings?.companyName || 'Yeni Kullanıcı Ekle'}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="E-posta"
              variant="outlined"
              name="email"
              value={newUser.email}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              disabled={editMode}
            />
            <TextField
              label="Şifre"
              variant="outlined"
              name="password"
              type="password"
              value={newUser.password}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="role-label">Rol</InputLabel>
              <Select
                labelId="role-label"
                id="role"
                name="role"
                value={newUser.role}
                label="Rol"
                onChange={handleInputChange}
                disabled={editMode}
              >
                <MenuItem value="user">Kullanıcı</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" color="primary" onClick={editMode ? handleUpdateUser : handleAddUser} fullWidth>
              {editMode ? 'Güncelle' : 'Ekle'}
            </Button>
            {editMode && (
              <Button variant="outlined" color="secondary" onClick={handleEditDialogClose} fullWidth sx={{ mt: 1 }}>
                İptal
              </Button>
            )}
          </Grid>
        </Grid>
      </StyledPaper>

      {/* Kullanıcı Düzenleme Popup */}
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose}>
        <DialogTitle>Kullanıcıyı Düzenle</DialogTitle>
        <DialogContent>
          <TextField
            label="E-posta"
            variant="outlined"
            name="email"
            value={newUser.email}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Şifre (değiştirilecekse doldurun)"
            variant="outlined"
            name="password"
            type="password"
            value={newUser.password}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="edit-role-label">Rol</InputLabel>
            <Select
              labelId="edit-role-label"
              id="edit-role"
              name="role"
              value={newUser.role}
              label="Rol"
              onChange={handleInputChange}
            >
              <MenuItem value="user">Kullanıcı</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>İptal</Button>
          <Button onClick={handleUpdateUser} variant="contained">Güncelle</Button>
        </DialogActions>
      </Dialog>

      {/* Bildirim (Toast) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {success || error}
        </Alert>
      </Snackbar>

      {/* Onay Diyaloğu */}
      {confirmDialogOpen && (
        <Dialog
          open={confirmDialogOpen}
          onClose={handleCloseConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {pendingAction === 'add' && 'Yeni Kullanıcı Ekleme Onayı'}
            {pendingAction === 'delete' && 'Kullanıcı Silme Onayı'}
            {pendingAction === 'update' && 'Kullanıcı Güncelleme Onayı'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {pendingAction === 'add' &&
                (`${pendingActionData?.email} e-posta adresli kullanıcıyı eklemek istediğinize emin misiniz?`)}
              {pendingAction === 'delete' &&
                (`ID'si ${pendingActionData?.userId} olan kullanıcıyı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)}
              {pendingAction === 'update' &&
                (`ID'si ${pendingActionData?.id} olan kullanıcının bilgilerini güncellemek istediğinize emin misiniz?`)}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirmDialog}>İptal</Button>
            <Button onClick={handleConfirmAction} autoFocus>
              Onayla
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default RegisterPage;